import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { CommonPageProps, NonUndefined, PromiseRejectedResult } from '@/types';
import DefaultLayout from '@/components/layout/DefaultLayout';
import { getCommonPageProps } from '@/api/common';
import Button from '@/components/shared/Button';
import Link from 'next/link';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import { useRouter } from 'next/router';
import TelegramSVG from '@/svg/telegram.svg?url';
import { ApiArticlesResponse, getArticles } from '@/api/articles';
import BlogList from '@/components/shared/BlogList';
import BlogFilters from '@/components/shared/BlogFilters';
import BlogSort from '@/components/shared/BlogSort';
import { ApiTagsResponse, getTags } from '@/api/tags';
import { ApiPageResponse, getPage } from '@/api/page';
import Image from 'next/image';

export const CASUAL_LIMIT = 10;
export const ROW_ITEMS_LIMIT = 3;

const emptyText = 'Статей пока нет, но&nbsp;появятся в&nbsp;ближайшее время.';

const CatalogPage = ({
    h1,
    articles,
    tags,
    subscribeLink,
    queryState,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const router = useRouter();
    useScrollRestoration(router);

    return (
        <DefaultLayout>
            <div className="wrapper">
                <Button
                    icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                    iconReverse={true}
                    tag={Link}
                    href="/"
                    geometryVariant="arrow"
                    className="catalog-to-main back-button-clickable"
                >
                    на главную
                </Button>
                <div className="blog-top">
                    <div className="blog-title">
                        <div className="blog-title__text h1">{h1}</div>
                        {articles.data && (
                            <div className="blog-title__count m-text-xs">{`[ ${articles.data.totalCount} ]`}</div>
                        )}
                    </div>
                    <div className="blog-subscribe catalog-subscribe-tg">
                        <div
                            className="catalog-subscribe-tg__text text-l"
                            dangerouslySetInnerHTML={{
                                __html: `От&nbsp;200&nbsp;$ до&nbsp;1000&nbsp;$&nbsp;&mdash; скидка на&nbsp;все изделия. Только для подписчиков нашего <nobr>Telegram-канала</nobr>.<br />
                                            Будь первым, кто узнает о&nbsp;новых поступлениях и&nbsp;эксклюзивных акциях.`,
                            }}
                        />
                        <Button
                            geometryVariant="mustache"
                            tag="a"
                            href={subscribeLink}
                            target="_blank"
                            rel="noreferrer"
                            icon={
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={TelegramSVG}
                                    alt=""
                                    width={14}
                                    height={14}
                                    className="img-fluid"
                                    decoding="async"
                                />
                            }
                            iconReverse
                            className="subscribe-tg-btn"
                        >
                            Подписаться
                        </Button>
                    </div>
                </div>
                {articles.data && articles.data.items.length > 0 ? (
                    <>
                        <div className="blog-filters">
                            {tags.data && tags.data.length > 1 && articles.data && (
                                <BlogFilters list={tags.data} totalCount={articles.data.totalCount} />
                            )}
                            {articles.data.items.length > ROW_ITEMS_LIMIT && <BlogSort />}
                        </div>
                        <div className="blog__list">
                            <BlogList
                                articles={articles.data.items}
                                initialData={articles}
                                initialQueryState={queryState}
                            />
                        </div>
                    </>
                ) : (
                    <div className="catalog-empty">
                        <div className="text-l" dangerouslySetInnerHTML={{ __html: emptyText }} />
                        <Button geometryVariant="mustache" tag={Link} href="/">
                            На главную
                        </Button>
                    </div>
                )}
            </div>
        </DefaultLayout>
    );
};

export default CatalogPage;

type CatalogPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        articles: ApiArticlesResponse;
        tags: ApiTagsResponse;
        queryState: Record<string, any>;
    }
>;

export const getServerSideProps: GetServerSideProps<CatalogPageProps> = async ({ query }) => {
    const { page, order, sort } = query;

    const tags = query['tags[]'];

    const queryState = {
        page: page ? Math.max(1, Number(page)) : 1,
        'tags[]': typeof tags === 'string' ? [tags] : tags || [],
        order: order ? order : null,
        sort: sort ? sort : null,
    };

    const data = await Promise.allSettled([
        getCommonPageProps(),
        getArticles({
            limit: CASUAL_LIMIT,
            'tags[]': queryState['tags[]'].map((item) => Number(item)),
            order: (queryState.order as string) || undefined,
            sort: (queryState.sort as string) || undefined,
            page: queryState.page,
        }),
        getTags(),
        getPage('blog'),
    ]);

    const [commonPageProps, articles, tagsResponse, pageInfo] = data;

    if (data.every((result) => result.status === 'fulfilled')) {
        const commonPagePropsSettled = commonPageProps as PromiseFulfilledResult<CommonPageProps>;
        const articlesSettled = articles as PromiseFulfilledResult<ApiArticlesResponse>;
        const tagsSettled = tagsResponse as PromiseFulfilledResult<ApiTagsResponse>;
        const pageInfoSettled = pageInfo as PromiseFulfilledResult<ApiPageResponse>;

        return {
            props: {
                ...commonPagePropsSettled.value,
                bodyClass: 'blog-page',
                meta: {
                    ...commonPagePropsSettled.value.meta,
                    title: 'Статьи',
                    ...pageInfoSettled.value.data?.meta,
                },
                h1: 'Статьи',
                articles: articlesSettled.value,
                tags: tagsSettled.value,
                queryState,
            } satisfies CatalogPageProps,
        };
    }

    for (const result in data) {
        if (typeof result === 'string') {
            throw new Error(result);
        } else {
            const rejectedResult = result as PromiseRejectedResult;
            if (rejectedResult.reason?.response?.status === 404) {
                return { notFound: true };
            }

            if (rejectedResult.reason instanceof Error) {
                throw rejectedResult.reason;
            }
        }
    }

    throw new Error('Internal server error');
};
